import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductNavbar from './ProductNavbar';
import LeftSidebarUpper from './LeftSidebarUpper';
import LeftSidebarDown from './LeftSidebarDown';
import RightProductSection from './RightProductSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Topwear = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState('recommended');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSortModal, setShowMobileSortModal] = useState(false);
  const [selectedMobileFilterCategory, setSelectedMobileFilterCategory] = useState('categories');
  const [selectedFilters, setSelectedFilters] = useState({
    gender: [],
    kids: [],
    price: [],
    sale: [],
    sports: [],
    colour: [],
    features: [],
    fit: [],
    brand: [],
    technology: [],
    bestFor: [],
    material: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedFilters, allProducts, sortBy]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?category=Fashion`);
      setAllProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applySorting = (products) => {
    let sorted = [...products];
    
    switch(sortBy) {
      case 'whats-new':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popularity':
        // You can add a popularity field later, for now just keep order
        break;
      case 'better-discount':
        // Sort by discount if you have discount field
        break;
      case 'price-high-low':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'price-low-high':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'customer-rating':
        // Sort by rating if you have rating field
        break;
      case 'recommended':
      default:
        // Keep default order
        break;
    }
    
    return sorted;
  };

  const applyFilters = () => {
    let filtered = [...allProducts];

    // Debug logging
    console.log('All Products:', allProducts.length);
    console.log('Selected Filters:', selectedFilters);
        // Filter by main_category
    filtered = filtered.filter(product => 
      product.main_category === 'Topwear'
    );
    // Gender filter - check both gender field and category fallback
    if (selectedFilters.gender.length > 0) {
      filtered = filtered.filter(product => {
        // If product has gender field, use it
        if (product.gender) {
          return selectedFilters.gender.includes(product.gender);
        }
        // Fallback: if no gender field, show all products (backward compatibility)
        return true;
      });
    }

    // Price filter
    if (selectedFilters.price.length > 0) {
      filtered = filtered.filter(product => {
        const price = product.price;
        return selectedFilters.price.some(range => {
          if (range === 'Under ₹ 2 500.00') return price < 2500;
          if (range === '₹ 2 501.00 - ₹ 7 500.00') return price >= 2501 && price <= 7500;
          if (range === '₹ 7 501.00 - ₹ 12 999.00') return price >= 7501 && price <= 12999;
          if (range === 'Over ₹ 13 000.00') return price > 13000;
          return false;
        });
      });
    }

    // Colour filter
    if (selectedFilters.colour.length > 0) {
      filtered = filtered.filter(product => 
        product.colour && product.colour.length > 0 && product.colour.some(c => selectedFilters.colour.includes(c))
      );
    }

    // Fit filter
    if (selectedFilters.fit.length > 0) {
      filtered = filtered.filter(product => 
        product.fit && selectedFilters.fit.includes(product.fit)
      );
    }

    // Brand filter
    if (selectedFilters.brand.length > 0) {
      filtered = filtered.filter(product => 
        product.brand && selectedFilters.brand.includes(product.brand)
      );
    }

    // Material filter
    if (selectedFilters.material.length > 0) {
      filtered = filtered.filter(product => 
        product.material && selectedFilters.material.includes(product.material)
      );
    }

    // Technology filter
    if (selectedFilters.technology.length > 0) {
      filtered = filtered.filter(product => 
        product.technology && selectedFilters.technology.includes(product.technology)
      );
    }

    // Sports filter
    if (selectedFilters.sports.length > 0) {
      filtered = filtered.filter(product => 
        product.sports && selectedFilters.sports.includes(product.sports)
      );
    }

    // Best For filter
    if (selectedFilters.bestFor.length > 0) {
      filtered = filtered.filter(product => 
        product.best_for && selectedFilters.bestFor.includes(product.best_for)
      );
    }

    // Features filter
    if (selectedFilters.features.length > 0) {
      filtered = filtered.filter(product => 
        product.features && product.features.length > 0 && product.features.some(f => selectedFilters.features.includes(f))
      );
    }

    console.log('Filtered Products:', filtered.length);
    
    // Apply sorting
    const sorted = applySorting(filtered);
    setFilteredProducts(sorted);
  };

  const handleFilterChange = (category, value) => {
    setSelectedFilters(prev => {
      const current = prev[category];
      const newValues = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [category]: newValues };
    });
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {/* Product-specific Navbar */}
      <ProductNavbar 
        category="Men Topwear" 
        breadcrumbs="Men Topwear"
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-16">
        <div className="flex gap-8 py-8">
          {/* Left Sidebar - Filters (Hidden on mobile) */}
          {showFilters && (
            <aside className="hidden lg:block w-64 flex-shrink-0">
              {/* Upper Section - Category Specific */}
              <LeftSidebarUpper category="topwear" />
              
              {/* Lower Section - Common Filters */}
              <LeftSidebarDown 
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
              />
            </aside>
          )}

          {/* Right Section - Products Grid */}
          <main className="flex-1">
            <RightProductSection products={filteredProducts} loading={loading} />
          </main>
        </div>
      </div>

      {/* Mobile Bottom Fixed Buttons */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 flex z-50">
        <button 
          onClick={() => setShowMobileSortModal(true)}
          className="flex-1 py-4 flex items-center justify-center gap-2 border-r border-gray-300"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
          <span className="font-semibold">SORT</span>
        </button>
        <button 
          onClick={() => setShowMobileFilters(true)}
          className="flex-1 py-4 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="font-semibold">FILTER</span>
        </button>
      </div>

      {/* Mobile Sort Modal */}
      {showMobileSortModal && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowMobileSortModal(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">SORT BY</h3>
                <button onClick={() => setShowMobileSortModal(false)}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-1">
                {[
                  { value: 'popularity', label: 'Popularity', icon: '🔥' },
                  { value: 'whats-new', label: 'Latest', icon: '⭐' },
                  { value: 'better-discount', label: 'Discount', icon: '💰' },
                  { value: 'price-high-low', label: 'Price: High to Low', icon: '₹' },
                  { value: 'price-low-high', label: 'Price: Low to High', icon: '₹' },
                  { value: 'customer-rating', label: 'Customer Rating', icon: '⭐' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setShowMobileSortModal(false);
                    }}
                    className={`w-full text-left px-4 py-4 flex items-center gap-3 hover:bg-gray-50 ${
                      sortBy === option.value ? 'text-pink-600 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-xl">{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 bg-white z-50 flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-300 px-4 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold">FILTERS</h3>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  setSelectedFilters({
                    gender: [],
                    kids: [],
                    price: [],
                    sale: [],
                    sports: [],
                    colour: [],
                    features: [],
                    fit: [],
                    brand: [],
                    technology: [],
                    bestFor: [],
                    material: []
                  });
                }}
                className="text-pink-600 font-semibold"
              >
                CLEAR ALL
              </button>
              <button onClick={() => setShowMobileFilters(false)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Split View - Categories and Options */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Side - Categories */}
            <div className="w-2/5 bg-gray-50 border-r border-gray-300 overflow-y-auto">
              <button
                onClick={() => setSelectedMobileFilterCategory('categories')}
                className={`w-full text-left px-4 py-4 border-b border-gray-200 flex items-center justify-between ${
                  selectedMobileFilterCategory === 'categories' ? 'bg-white border-l-4 border-l-pink-600 font-semibold' : ''
                }`}
              >
                <span className="text-sm">Categories</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {[
                { id: 'gender', label: 'Gender', count: selectedFilters.gender.length },
                { id: 'kids', label: 'Kids', count: selectedFilters.kids.length },
                { id: 'price', label: 'Shop By Price', count: selectedFilters.price.length },
                { id: 'sale', label: 'Sale & Offers', count: selectedFilters.sale.length },
                { id: 'sports', label: 'Sports', count: selectedFilters.sports.length },
                { id: 'colour', label: 'Colour', count: selectedFilters.colour.length },
                { id: 'features', label: 'Features', count: selectedFilters.features.length },
                { id: 'fit', label: 'Fit', count: selectedFilters.fit.length },
                { id: 'brand', label: 'Brand', count: selectedFilters.brand.length },
                { id: 'technology', label: 'Technology', count: selectedFilters.technology.length },
                { id: 'bestFor', label: 'Best For', count: selectedFilters.bestFor.length },
                { id: 'material', label: 'Material', count: selectedFilters.material.length }
              ].map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedMobileFilterCategory(category.id)}
                  className={`w-full text-left px-4 py-4 border-b border-gray-200 flex items-center justify-between ${
                    selectedMobileFilterCategory === category.id ? 'bg-white border-l-4 border-l-pink-600 font-semibold' : ''
                  }`}
                >
                  <span className="text-sm">{category.label}</span>
                  {category.count > 0 && (
                    <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded-full mr-2">{category.count}</span>
                  )}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>

            {/* Right Side - Filter Options */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedMobileFilterCategory === 'categories' && (
                <div className="space-y-2">
                  {['T-Shirts', 'Casual Shirts', 'Formal Shirts', 'Sweatshirts', 'Sweaters', 'Jackets', 'Blazers & Coats', 'Suits', 'Rain Jackets'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input type="checkbox" className="mr-3 w-5 h-5" />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'gender' && (
                <div className="space-y-2">
                  {['Men', 'Women', 'Unisex'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.gender.includes(item)}
                        onChange={() => handleFilterChange('gender', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'kids' && (
                <div className="space-y-2">
                  {['Boys', 'Girls'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.kids.includes(item)}
                        onChange={() => handleFilterChange('kids', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'price' && (
                <div className="space-y-2">
                  {[
                    'Under ₹ 2 500.00',
                    '₹ 2 501.00 - ₹ 7 500.00',
                    '₹ 7 501.00 - ₹ 12 999.00',
                    'Over ₹ 13 000.00'
                  ].map((range) => (
                    <label key={range} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.price.includes(range)}
                        onChange={() => handleFilterChange('price', range)}
                      />
                      <span className="text-sm">{range}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'sale' && (
                <div className="space-y-2">
                  <label className="flex items-center py-2">
                    <input 
                      type="checkbox" 
                      className="mr-3 w-5 h-5"
                      checked={selectedFilters.sale.includes('Sale')}
                      onChange={() => handleFilterChange('sale', 'Sale')}
                    />
                    <span className="text-sm">Sale</span>
                  </label>
                </div>
              )}

              {selectedMobileFilterCategory === 'sports' && (
                <div className="space-y-2">
                  {['Lifestyle', 'Running', 'Football', 'Training & Gym'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.sports.includes(item)}
                        onChange={() => handleFilterChange('sports', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'colour' && (
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { name: 'Black', color: '#000000' },
                    { name: 'Blue', color: '#0080FF' },
                    { name: 'Brown', color: '#8B4513' },
                    { name: 'Green', color: '#90EE90' },
                    { name: 'Grey', color: '#808080' },
                    { name: 'Multi-Colour', color: '#FFFFFF', pattern: true },
                    { name: 'Orange', color: '#FF6600' },
                    { name: 'Pink', color: '#FF69B4' },
                    { name: 'Purple', color: '#800080' },
                    { name: 'Red', color: '#FF0000' },
                    { name: 'White', color: '#FFFFFF' },
                    { name: 'Yellow', color: '#FFFF00' }
                  ].map((item) => (
                    <div 
                      key={item.name} 
                      className="flex flex-col items-center cursor-pointer"
                      onClick={() => handleFilterChange('colour', item.name)}
                    >
                      <div 
                        className={`w-12 h-12 rounded-full border-2 ${
                          selectedFilters.colour.includes(item.name) ? 'border-pink-600 ring-2 ring-pink-200' : 'border-gray-300'
                        }`}
                        style={{ 
                          backgroundColor: item.color,
                          backgroundImage: item.pattern ? 'radial-gradient(circle, black 20%, transparent 20%), radial-gradient(circle, black 20%, transparent 20%)' : 'none',
                          backgroundSize: item.pattern ? '8px 8px' : 'auto',
                          backgroundPosition: item.pattern ? '0 0, 4px 4px' : '0 0'
                        }}
                      />
                      <span className="text-xs mt-1 text-center">{item.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'features' && (
                <div className="space-y-2">
                  {['Vented', 'Drawcord', 'Longline', 'Non-Padded Cups'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.features.includes(item)}
                        onChange={() => handleFilterChange('features', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'fit' && (
                <div className="space-y-2">
                  {['Oversized', 'Loose', 'Standard', 'Slim'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.fit.includes(item)}
                        onChange={() => handleFilterChange('fit', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'brand' && (
                <div className="space-y-2">
                  {['Nike Sportswear', 'Jordan', 'Kobe', 'NikeLab'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.brand.includes(item)}
                        onChange={() => handleFilterChange('brand', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'technology' && (
                <div className="space-y-2">
                  {['Dri-FIT', 'Dri-FIT ADV', 'Storm-FIT', 'Therma-FIT'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.technology.includes(item)}
                        onChange={() => handleFilterChange('technology', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'bestFor' && (
                <div className="space-y-2">
                  {['Winter Wear', 'Warm Weather', 'Wet Weather Conditions'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.bestFor.includes(item)}
                        onChange={() => handleFilterChange('bestFor', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedMobileFilterCategory === 'material' && (
                <div className="space-y-2">
                  {['Fleece', 'Tech Fleece', 'Recycled Materials', 'InfinaSoft'].map((item) => (
                    <label key={item} className="flex items-center py-2">
                      <input 
                        type="checkbox" 
                        className="mr-3 w-5 h-5"
                        checked={selectedFilters.material.includes(item)}
                        onChange={() => handleFilterChange('material', item)}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer - Apply Button */}
          <div className="sticky bottom-0 bg-white border-t border-gray-300 p-4">
            <button 
              onClick={() => setShowMobileFilters(false)}
              className="w-full bg-pink-600 text-white py-4 rounded font-semibold"
            >
              APPLY
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Topwear;
